import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
    DEFAULT_BATTERY_VALUES,
    DEFAULT_GB_ASSET_CONFIGURATION,
    DEFAULT_GERMAN_ASSET_CONFIGURATION,
} from '@/constants/forecasting/assetConstants.ts';
import type {
    IBatteryInputData,
    IBatteryInputObject,
    IAvailabilityInputData,
} from '@/interfaces/storageForecasting/IBatteryInputData.ts';
import type {
    IAssetCoreInput,
    IAssetInputDto,
    IAssetInputObject,
    IAssetStorecastBase,
    IAssetStorecastData,
    IAssetStorecastGB,
    IAssetStorecastGermany,
    TAssetCoreVariant,
    TAssetTechnology,
} from '@/interfaces/storageForecasting/IAsset';
import { isStorageForecastCountry } from '@/types/TStorageForecastCountries.ts';
import type { TStorageForecastCountries } from '@/types/TStorageForecastCountries.ts';
import { hoursInDay, monthsInYear } from '@/utils/storageForecasting/assetValidationUtils.ts';
import {
    mapAssetTypesToStorecastOnsiteOption,
    type StorecastColocationOnsiteValue,
} from '@/utils/storageForecasting/assetUtils.ts';

dayjs.extend(utc);

function defaultInputObject (value = 0): IAssetInputObject {
    return { values: [value], granularity: 'Year' };
}

function defaultNetworkAvailabilityValues (): Array<Array<number>> {
    return Array.from({ length: monthsInYear }, () => Array.from({ length: hoursInDay }, () => 1));
}

function defaultNetworkExportAvailability (): IAvailabilityInputData {
    return { values: defaultNetworkAvailabilityValues() };
}

function defaultSiteRampRestriction (): IBatteryInputObject {
    return {
        values: [DEFAULT_BATTERY_VALUES.defaultRampRateRestriction],
        granularity: 'Year',
    };
}

function defaultFrCapacityLimit (): IBatteryInputObject {
    return {
        values: [DEFAULT_BATTERY_VALUES.defaultFrCapacityRestriction],
        granularity: 'Year',
    };
}

function filterTechnologyList (types: ReadonlyArray<TAssetTechnology>): Array<TAssetTechnology> {
    return types.filter((t): t is TAssetTechnology => t === 'Solar' || t === 'Wind');
}

function cloneInputObject (
    input: IAssetInputObject | IBatteryInputObject | null | undefined,
): IAssetInputObject | null {
    if (!input || typeof input !== 'object' || !('values' in input)) {
        return null;
    }
    const cloned: IAssetInputObject = {
        granularity: input.granularity,
        values: [...input.values],
    };
    if (input.degradationMode !== undefined) {
        cloned.degradationMode = input.degradationMode;
    }
    if (input.initialValue !== undefined) {
        cloned.initialValue = input.initialValue;
    }
    if (input.cycleSeries) {
        cloned.cycleSeries = input.cycleSeries.map((s) => ({
            numberOfCycles: s.numberOfCycles,
            values: [...s.values],
        }));
    }
    return cloned;
}

function cloneNetworkExportAvailability (
    availability: IAvailabilityInputData | null | undefined,
): IAvailabilityInputData | null {
    if (!availability?.values) {
        return null;
    }
    return { values: availability.values.map((row) => [...row]) };
}

interface IColocatedOnsiteCapacityFields {
    onsiteCapacity: IAssetInputObject | null;
    onsiteWindCapacity: IAssetInputObject | null;
    onsiteSolarCapacity: IAssetInputObject | null;
}

/** Solar/wind-only assets store capacity on granular fields; battery colocated forms use `onsiteCapacity` for single-RES options. */
function resolveColocatedOnsiteCapacityFields (
    option: StorecastColocationOnsiteValue,
    source: Pick<IAssetStorecastData, 'onsiteCapacity' | 'onsiteWindCapacity' | 'onsiteSolarCapacity'>,
): IColocatedOnsiteCapacityFields {
    const onsiteCapacity = cloneInputObject(source.onsiteCapacity);
    const onsiteWindCapacity = cloneInputObject(source.onsiteWindCapacity);
    const onsiteSolarCapacity = cloneInputObject(source.onsiteSolarCapacity);

    if (option === 'Standalone') {
        return { onsiteCapacity: null, onsiteWindCapacity: null, onsiteSolarCapacity: null };
    }
    if (option === 'WindAndSolar') {
        return {
            onsiteCapacity: null,
            onsiteWindCapacity: onsiteWindCapacity ?? defaultInputObject(0),
            onsiteSolarCapacity: onsiteSolarCapacity ?? defaultInputObject(0),
        };
    }
    if (option === 'Solar') {
        return {
            onsiteCapacity: onsiteCapacity ?? onsiteSolarCapacity ?? defaultInputObject(0),
            onsiteWindCapacity: null,
            onsiteSolarCapacity: null,
        };
    }
    return {
        onsiteCapacity: onsiteCapacity ?? onsiteWindCapacity ?? defaultInputObject(0),
        onsiteWindCapacity: null,
        onsiteSolarCapacity: null,
    };
}

/** When creating a battery model from solar/wind, default site limits/inverters still at 0 — align with TEC export. */
function alignBatterySiteCapacitiesWithExport (data: IBatteryInputData): IBatteryInputData {
    const exportFirst = data.exportCapacity?.values[0] ?? 0;
    if (exportFirst <= 0) return data;

    const alignIfUnset = (field: IBatteryInputObject | null | undefined): IBatteryInputObject => {
        const current = field ?? defaultInputObject(0);
        if ((current.values[0] ?? 0) > 0) {
            return { ...current, values: [...current.values] };
        }
        return { ...current, values: [exportFirst], granularity: current.granularity ?? 'Year' };
    };

    return {
        ...data,
        importCapacity: alignIfUnset(data.importCapacity),
        inverterCapacityBattery: alignIfUnset(data.inverterCapacityBattery),
        inverterCapacityOnsiteGen: alignIfUnset(data.inverterCapacityOnsiteGen),
    };
}

export function getAssetCoreCountry (data: IAssetCoreInput): TStorageForecastCountries {
    const { country } = data;
    if (isStorageForecastCountry(country)) {
        return country;
    }
    throw new Error(`Unknown or missing country on asset input: ${String(country)}`);
}

export function resolveAssetCoreVariant (data: IAssetCoreInput): TAssetCoreVariant {
    const country = getAssetCoreCountry(data);
    const hasBattery = (data.technologyList ?? []).includes('Battery');

    if (country === 'GB' && hasBattery) return 'batteryGb';
    if (country === 'Germany' && hasBattery) return 'batteryGermany';
    if (country === 'GB') return 'assetGb';
    if (country === 'Germany') return 'assetGermany';

    throw new Error(`Unknown asset core variant for country '${country}'`);
}

function createAssetStorecastBase (
    country: TStorageForecastCountries,
    technologyList: Array<TAssetTechnology>,
): IAssetStorecastBase {
    const storecastOnsiteOption = mapAssetTypesToStorecastOnsiteOption(technologyList);
    const hasWind = technologyList.includes('Wind');
    const hasSolar = technologyList.includes('Solar');

    return {
        countryName: country,
        name: '',
        startDate: dayjs().startOf('year').toISOString(),
        endDateInclusive: dayjs.utc().add(1, 'year').startOf('year').toISOString(),
        vom: defaultInputObject(0),
        technologyList,
        coupling: 'AC',
        inverterCapacityOnsiteGen: defaultInputObject(0),
        inverterEfficiencyOnsiteGen: defaultInputObject(100),
        exportCapacity: defaultInputObject(0),
        networkExportAvailability: defaultNetworkExportAvailability(),
        storecastOnsiteOption,
        onsiteCapacity: null,
        regionToGrabFromDb: '',
        onsiteWindCapacity: hasWind ? defaultInputObject(0) : null,
        onsiteSolarCapacity: hasSolar ? defaultInputObject(0) : null,
        assetPolicySupportType: 'None',
        assetHasCfd: false,
        cfdStrikePrice: defaultInputObject(0),
        cfdContractLength: DEFAULT_BATTERY_VALUES.defaultCfdContractLength,
        cfdContractStart: dayjs.utc().startOf('year').toISOString(),
        competeInDayAhead: true,
        applyCustomResLoadFactor: false,
        customResLoadFactor: null,
        gridConnection: 'Transmission',
    };
}

export function createDefaultAssetStorecastFormModel (
    country: TStorageForecastCountries,
    selectedAssetTypes: ReadonlyArray<TAssetTechnology>,
): IAssetStorecastData {
    const technologyList = filterTechnologyList(selectedAssetTypes);
    const base = createAssetStorecastBase(country, technologyList);

    if (country === 'Germany') {
        return {
            ...base,
            country: 'Germany',
        } satisfies IAssetStorecastGermany;
    }

    return {
        ...base,
        country: 'GB',
        cmContract: 'None',
        cmContractStart: dayjs().startOf('month').toISOString(),
        tnuosZone: '',
        tlfZone: '',
        overrideDuos: false,
        duosCapacityCharge: defaultInputObject(0),
        duosImportCharge: defaultInputObject(0),
        duosExportCredit: defaultInputObject(0),
    } satisfies IAssetStorecastGB;
}

function mergeStorecastFieldsFromCore (
    defaults: IAssetStorecastData,
    d: IAssetCoreInput,
): IAssetStorecastData {
    const technologyList = (d.technologyList?.length ?? 0) > 0
        ? d.technologyList!
        : defaults.technologyList;

    const merged: IAssetStorecastData = {
        ...defaults,
        countryName: getAssetCoreCountry(d),
        name: d.name,
        startDate: d.startDate,
        endDateInclusive: d.endDateInclusive,
        vom: d.vom,
        technologyList,
        coupling: d.coupling,
        inverterCapacityOnsiteGen: d.inverterCapacityOnsiteGen,
        inverterEfficiencyOnsiteGen: {
            ...d.inverterEfficiencyOnsiteGen,
            values: [...d.inverterEfficiencyOnsiteGen.values.map((i) => i * 100)],
        },
        exportCapacity: d.exportCapacity,
        networkExportAvailability: d.networkExportAvailability
            ? { values: d.networkExportAvailability.values.map((inner) => [...inner]) }
            : defaults.networkExportAvailability,
        storecastOnsiteOption: d.storecastOnsiteOption || defaults.storecastOnsiteOption,
        regionToGrabFromDb: d.regionToGrabFromDb,
        onsiteCapacity: d.onsiteCapacity,
        assetPolicySupportType: d.assetPolicySupportType ?? defaults.assetPolicySupportType,
        assetHasCfd: d.assetHasCfd ?? defaults.assetHasCfd,
        cfdStrikePrice: d.cfdStrikePrice
            ? { ...d.cfdStrikePrice, values: [...d.cfdStrikePrice.values] }
            : defaults.cfdStrikePrice,
        cfdContractLength: d.cfdContractLength ?? defaults.cfdContractLength,
        cfdContractStart: d.cfdContractStart ?? defaults.cfdContractStart,
        competeInDayAhead: d.competeInDayAhead ?? defaults.competeInDayAhead,
        onsiteWindCapacity: d.onsiteWindCapacity ?? defaults.onsiteWindCapacity,
        onsiteSolarCapacity: d.onsiteSolarCapacity ?? defaults.onsiteSolarCapacity,
        applyCustomResLoadFactor: d.applyCustomResLoadFactor ?? defaults.applyCustomResLoadFactor,
        customResLoadFactor: {
            ...(d.customResLoadFactor ?? defaults.customResLoadFactor!),
            values: [...(d.customResLoadFactor ?? defaults.customResLoadFactor!).values.map((i) => i * 100)],
        },
        gridConnection: d.gridConnection ?? defaults.gridConnection,
    };

    if (merged.country === 'GB') {
        return {
            ...merged,
            cmContract: d.cmContract ?? merged.cmContract,
            cmContractStart: d.cmContractStart ?? merged.cmContractStart,
            tnuosZone: d.tnuosZone ?? merged.tnuosZone,
            tlfZone: d.tlfZone ?? merged.tlfZone,
            overrideDuos: d.overrideDuos ?? merged.overrideDuos,
            duosCapacityCharge: d.duosCapacityCharge ?? merged.duosCapacityCharge,
            duosImportCharge: d.duosImportCharge ?? merged.duosImportCharge,
            duosExportCredit: d.duosExportCredit ?? merged.duosExportCredit,
        };
    }

    return merged;
}

/**
 * Maps list DTO (catchall AssetCore JSON) → standalone solar/wind form model.
 */
export function toAssetStorecastFormModel (
    asset: IAssetInputDto,
    selectedAssetTypes: ReadonlyArray<TAssetTechnology>,
): IAssetStorecastData {
    const country = getAssetCoreCountry(asset.data);
    const defaults = createDefaultAssetStorecastFormModel(country, selectedAssetTypes);
    return mergeStorecastFieldsFromCore(defaults, asset.data);
}

function scaleInputObjectValues (
    input: IAssetInputObject | null | undefined,
    factor: number,
    fallback: IBatteryInputObject,
): IBatteryInputObject {
    if (!input || typeof input !== 'object' || !('values' in input)) {
        return fallback;
    }
    return {
        ...input,
        values: [...input.values.map((i) => i * factor)],
    };
}

function normaliseDegradationInputForForm (
    input: unknown,
    fallback: IBatteryInputObject,
    options?: { scaleValues?: number },
): IBatteryInputObject {
    const scale = options?.scaleValues ?? 1;

    if (input && typeof input === 'object' && !Array.isArray(input) && 'values' in input) {
        const typed = input as IAssetInputObject;
        const scaledCycleSeries = typed.cycleSeries?.map((s) => ({
            numberOfCycles: s.numberOfCycles,
            values: s.values.map((v) => v * scale),
        }));
        const scaledValues = [...typed.values.map((i) => i * scale)];
        const dynamicInitial = typed.initialValue !== undefined
            ? typed.initialValue * scale
            : (scaledValues[0] ?? 0);

        const hydratedCycleSeries = (typed.degradationMode === 'DynamicCycles' && (!scaledCycleSeries || scaledCycleSeries.length === 0) && scaledValues.length > 1)
            ? [{ numberOfCycles: 0, values: scaledValues.slice(1) }]
            : scaledCycleSeries;

        const hydratedValues = (typed.degradationMode === 'DynamicCycles' && scaledValues.length === 0 && scaledCycleSeries?.length)
            ? [dynamicInitial, ...scaledCycleSeries[0].values]
            : scaledValues;

        return {
            ...typed,
            granularity: typed.granularity ?? 'Year',
            values: hydratedValues,
            initialValue: dynamicInitial,
            cycleSeries: hydratedCycleSeries,
        };
    }

    if (Array.isArray(input) && input.length > 0) {
        const profiles = input as Array<{
            averageCycles?: number;
            degradationProfile?: { values?: number[]; granularity?: string };
            degradation?: { values?: number[]; granularity?: string };
        }>;

        const firstProfile = profiles[0];
        const firstDegradation = firstProfile.degradationProfile ?? firstProfile.degradation;
        const firstValues = Array.isArray(firstDegradation?.values)
            ? firstDegradation.values.map((v) => v * scale)
            : [...fallback.values];
        const initialValue = firstValues[0] ?? fallback.values[0] ?? 0;

        return {
            ...fallback,
            granularity: firstDegradation?.granularity ?? fallback.granularity ?? 'Year',
            values: firstValues,
            degradationMode: 'DynamicCycles',
            initialValue,
            cycleSeries: profiles.map((profile) => {
                const profileDegradation = profile.degradationProfile ?? profile.degradation;
                const profileValues = Array.isArray(profileDegradation?.values)
                    ? profileDegradation.values.map((v) => v * scale)
                    : [initialValue];
                return {
                    numberOfCycles: profile.averageCycles ?? 0,
                    values: profileValues.slice(1),
                };
            }),
        };
    }

    return fallback;
}

/**
 * Maps list DTO (catchall AssetCore JSON) → battery form model (`BatteryAssetFields`).
 */
export function toBatteryFormModel (asset: IAssetInputDto): IBatteryInputData {
    const d = asset.data;
    const country = getAssetCoreCountry(d);
    const variant = resolveAssetCoreVariant(d);
    const defaults = country === 'Germany'
        ? DEFAULT_GERMAN_ASSET_CONFIGURATION
        : DEFAULT_GB_ASSET_CONFIGURATION;

    const rampDownDefault = defaultSiteRampRestriction();
    const rampUpDefault = defaultSiteRampRestriction();

    const frCapacityLimitDefault = defaultFrCapacityLimit();

    const exportCapacity: IBatteryInputObject = d.exportCapacity && typeof d.exportCapacity === 'object' && 'values' in d.exportCapacity
        ? d.exportCapacity
        : { values: [0], granularity: 'Year' };

    const competeInBalancing = variant === 'batteryGermany'
        ? (d.competeInAncillaries ?? d.competeInBalancing ?? defaults.competeInBalancing)
        : (d.competeInBalancing ?? defaults.competeInBalancing);

    const runAsLongDuration = variant === 'batteryGermany'
        ? (d.allowInterdayCycling ?? defaults.runAsLongDuration)
        : (d.runAsLongDuration ?? defaults.runAsLongDuration);

    const duration = normaliseDegradationInputForForm(d.duration, defaults.duration);
    const efficiency = normaliseDegradationInputForForm(d.efficiency, defaults.efficiency, { scaleValues: 100 });

    return {
        ...defaults,
        id: asset.id,
        country,
        name: d.name,
        startDate: d.startDate,
        endDateInclusive: d.endDateInclusive,
        vom: d.vom,
        coupling: d.coupling,
        competeInIntraday: d.competeInIntraday ?? defaults.competeInIntraday,
        competeInDayAhead: d.competeInDayAhead ?? defaults.competeInDayAhead,
        competeInBalancing,
        competeInAncillaries: variant === 'batteryGermany'
            ? competeInBalancing
            : defaults.competeInAncillaries,
        canRunFr: d.canRunFr ?? defaults.canRunFr,
        competeInTwoStageFr: d.competeInTwoStageFr ?? defaults.competeInTwoStageFr,
        allowInterdayCycling: variant === 'batteryGermany'
            ? runAsLongDuration
            : defaults.allowInterdayCycling,
        runAsLongDuration,
        duration,
        efficiency,
        dischargeCapacity: d.dischargeCapacity ?? defaults.dischargeCapacity,
        chargeCapacity: d.chargeCapacity ?? defaults.chargeCapacity,
        maxAverageCycles: d.maxAverageCycles ?? defaults.maxAverageCycles,
        applyMaxCyclesPerDay: d.applyMaxCyclesPerDay ?? defaults.applyMaxCyclesPerDay,
        maxCyclesPerDay: d.maxCyclesPerDay
            ? { ...d.maxCyclesPerDay, values: [...d.maxCyclesPerDay.values] }
            : defaults.maxCyclesPerDay,
        minimumSpread: d.minimumSpread
            ? { ...d.minimumSpread, values: [...d.minimumSpread.values] }
            : defaults.minimumSpread,
        inverterCapacityBattery: d.inverterCapacityBattery ?? defaults.inverterCapacityBattery,
        inverterEfficiencyBattery: scaleInputObjectValues(
            d.inverterEfficiencyBattery,
            100,
            defaults.inverterEfficiencyBattery,
        ),
        inverterCapacityOnsiteGen: d.inverterCapacityOnsiteGen ?? defaults.inverterCapacityOnsiteGen,
        inverterEfficiencyOnsiteGen: scaleInputObjectValues(
            d.inverterEfficiencyOnsiteGen,
            100,
            defaults.inverterEfficiencyOnsiteGen,
        ),
        importCapacity: d.importCapacity ?? defaults.importCapacity,
        exportCapacity,
        gridConnection: d.gridConnection ?? defaults.gridConnection,
        regionToGrabFromDb: d.regionToGrabFromDb ?? defaults.regionToGrabFromDb,
        storecastOnsiteOption: d.storecastOnsiteOption ?? defaults.storecastOnsiteOption,
        onsiteCapacity: d.onsiteCapacity ?? defaults.onsiteCapacity,
        onsiteWindCapacity: d.onsiteWindCapacity ?? defaults.onsiteWindCapacity,
        onsiteSolarCapacity: d.onsiteSolarCapacity ?? defaults.onsiteSolarCapacity,
        applyCustomResLoadFactor: d.applyCustomResLoadFactor ?? defaults.applyCustomResLoadFactor,
        customResLoadFactor: d.customResLoadFactor
            ? scaleInputObjectValues(d.customResLoadFactor, 100, defaults.customResLoadFactor)
            : defaults.customResLoadFactor,
        assetPolicySupportType: d.assetPolicySupportType ?? defaults.assetPolicySupportType,
        assetHasCfd: d.assetHasCfd,
        cfdStrikePrice: d.cfdStrikePrice ?? defaults.cfdStrikePrice,
        cfdContractLength: d.cfdContractLength ?? DEFAULT_BATTERY_VALUES.defaultCfdContractLength,
        cfdContractStart: d.cfdContractStart ?? defaults.cfdContractStart,
        cmContract: d.cmContract ?? defaults.cmContract,
        cmContractStart: d.cmContractStart ?? defaults.cmContractStart,
        tnuosZone: d.tnuosZone ?? defaults.tnuosZone,
        tlfZone: d.tlfZone ?? defaults.tlfZone,
        overrideDuos: d.overrideDuos ?? defaults.overrideDuos,
        duosCapacityCharge: d.duosCapacityCharge ?? defaults.duosCapacityCharge,
        duosImportCharge: d.duosImportCharge ?? defaults.duosImportCharge,
        duosExportCredit: d.duosExportCredit ?? defaults.duosExportCredit,
        locationalBalancingRegion: d.locationalBalancingRegion ?? defaults.locationalBalancingRegion,
        runLocationalBalancing: d.runLocationalBalancing ?? true,
        competeInLocationalBalancing: d.competeInLocationalBalancing ?? true,
        refurbishAge: d.refurbishAge ?? defaults.refurbishAge,
        siteRampUpRestriction: d.siteRampUpRestriction
            ? scaleInputObjectValues(d.siteRampUpRestriction, 100, rampUpDefault)
            : rampUpDefault,
        siteRampDownRestriction: d.siteRampDownRestriction
            ? scaleInputObjectValues(d.siteRampDownRestriction, 100, rampDownDefault)
            : rampDownDefault,
        siteHasRampRestrictions: !!d.siteRampUpRestriction || !!d.siteRampDownRestriction,
        applyRampRatesToAllStageFr: d.applyRampRatesToAllStageFr ?? defaults.applyRampRatesToAllStageFr,
        applyRampRatesToTwoStageFr: d.applyRampRatesToTwoStageFr ?? defaults.applyRampRatesToTwoStageFr,
        frCapacityRestrictionMode: d.frCapacityRestrictionMode ?? defaults.frCapacityRestrictionMode,
        maxPercentageCapacityAllowedInAllStageFr: d.maxPercentageCapacityAllowedInAllStageFr
            ? scaleInputObjectValues(
                d.maxPercentageCapacityAllowedInAllStageFr,
                100,
                frCapacityLimitDefault,
            )
            : frCapacityLimitDefault,
        maxPercentageCapacityAllowedInTwoStageFr: d.maxPercentageCapacityAllowedInTwoStageFr
            ? scaleInputObjectValues(
                d.maxPercentageCapacityAllowedInTwoStageFr,
                100,
                frCapacityLimitDefault,
            )
            : frCapacityLimitDefault,
        maxPercentageCapacityAllowedInFrMarketsCombined: d.maxPercentageCapacityAllowedInFrMarketsCombined
            ? scaleInputObjectValues(
                d.maxPercentageCapacityAllowedInFrMarketsCombined,
                100,
                frCapacityLimitDefault,
            )
            : frCapacityLimitDefault,
        networkExportAvailability: {
            values: d.networkExportAvailability
                ? d.networkExportAvailability.values.map((inner) => [...inner])
                : defaultNetworkAvailabilityValues(),
        },
        networkImportAvailability: {
            values: d.networkImportAvailability
                ? d.networkImportAvailability.values.map((inner) => [...inner])
                : defaultNetworkAvailabilityValues(),
        },
    };
}

/**
 * Copies shared storecast/battery form fields from a battery model onto a solar/wind model.
 */
export function applySharedFieldsFromBatteryToSolarWind (
    battery: IBatteryInputData,
    solarWind: IAssetStorecastData | null,
    country: TStorageForecastCountries,
    selectedTypes: ReadonlyArray<TAssetTechnology>,
): IAssetStorecastData {
    const technologyList = filterTechnologyList(selectedTypes);
    const hasWind = technologyList.includes('Wind');
    const hasSolar = technologyList.includes('Solar');
    const storecastOnsiteOption = mapAssetTypesToStorecastOnsiteOption(selectedTypes);
    const target = solarWind ?? createDefaultAssetStorecastFormModel(country, selectedTypes);

    const merged: IAssetStorecastData = {
        ...target,
        name: battery.name,
        startDate: battery.startDate,
        endDateInclusive: battery.endDateInclusive,
        vom: cloneInputObject(battery.vom) ?? target.vom,
        coupling: battery.coupling,
        technologyList,
        storecastOnsiteOption,
        exportCapacity: cloneInputObject(battery.exportCapacity) ?? target.exportCapacity,
        networkExportAvailability: cloneNetworkExportAvailability(battery.networkExportAvailability)
            ?? target.networkExportAvailability,
        gridConnection: battery.gridConnection ?? target.gridConnection,
        regionToGrabFromDb: battery.regionToGrabFromDb ?? target.regionToGrabFromDb,
        inverterCapacityOnsiteGen: cloneInputObject(battery.inverterCapacityOnsiteGen) ?? target.inverterCapacityOnsiteGen,
        inverterEfficiencyOnsiteGen: cloneInputObject(battery.inverterEfficiencyOnsiteGen) ?? target.inverterEfficiencyOnsiteGen,
        onsiteCapacity: cloneInputObject(battery.onsiteCapacity),
        onsiteWindCapacity: hasWind
            ? (cloneInputObject(battery.onsiteWindCapacity) ?? target.onsiteWindCapacity ?? defaultInputObject(0))
            : null,
        onsiteSolarCapacity: hasSolar
            ? (cloneInputObject(battery.onsiteSolarCapacity) ?? target.onsiteSolarCapacity ?? defaultInputObject(0))
            : null,
        assetPolicySupportType: battery.assetPolicySupportType ?? target.assetPolicySupportType,
        assetHasCfd: battery.assetHasCfd,
        cfdStrikePrice: cloneInputObject(battery.cfdStrikePrice) ?? target.cfdStrikePrice,
        cfdContractLength: battery.cfdContractLength,
        cfdContractStart: battery.cfdContractStart ?? target.cfdContractStart,
        competeInDayAhead: battery.competeInDayAhead,
        applyCustomResLoadFactor: battery.applyCustomResLoadFactor,
        customResLoadFactor: cloneInputObject(battery.customResLoadFactor),
    };

    if (country === 'GB' && merged.country === 'GB') {
        return {
            ...merged,
            cmContract: battery.cmContract ?? merged.cmContract,
            cmContractStart: battery.cmContractStart ?? merged.cmContractStart,
            tnuosZone: battery.tnuosZone ?? merged.tnuosZone,
            tlfZone: battery.tlfZone ?? merged.tlfZone,
            overrideDuos: battery.overrideDuos ?? merged.overrideDuos,
            duosCapacityCharge: cloneInputObject(battery.duosCapacityCharge) ?? merged.duosCapacityCharge,
            duosImportCharge: cloneInputObject(battery.duosImportCharge) ?? merged.duosImportCharge,
            duosExportCredit: cloneInputObject(battery.duosExportCredit) ?? merged.duosExportCredit,
        };
    }

    return merged;
}

/**
 * Copies shared storecast/battery form fields from a solar/wind model onto a battery model.
 */
export function applySharedFieldsFromSolarWindToBattery (
    solarWind: IAssetStorecastData,
    battery: IBatteryInputData | null,
    country: TStorageForecastCountries,
    selectedTypes: ReadonlyArray<TAssetTechnology>,
): IBatteryInputData {
    const defaults = country === 'Germany'
        ? DEFAULT_GERMAN_ASSET_CONFIGURATION
        : DEFAULT_GB_ASSET_CONFIGURATION;
    const target = battery ?? { ...defaults, id: null, country };
    const storecastOnsiteOption = mapAssetTypesToStorecastOnsiteOption(selectedTypes);
    const colocatedOnsiteFields = resolveColocatedOnsiteCapacityFields(storecastOnsiteOption, solarWind);

    const merged: IBatteryInputData = {
        ...target,
        name: solarWind.name,
        startDate: solarWind.startDate,
        endDateInclusive: solarWind.endDateInclusive,
        vom: cloneInputObject(solarWind.vom) ?? target.vom,
        coupling: solarWind.coupling,
        storecastOnsiteOption,
        exportCapacity: cloneInputObject(solarWind.exportCapacity) ?? target.exportCapacity,
        networkExportAvailability: cloneNetworkExportAvailability(solarWind.networkExportAvailability)
            ?? target.networkExportAvailability,
        gridConnection: solarWind.gridConnection,
        regionToGrabFromDb: solarWind.regionToGrabFromDb,
        inverterCapacityOnsiteGen: cloneInputObject(solarWind.inverterCapacityOnsiteGen) ?? target.inverterCapacityOnsiteGen,
        inverterEfficiencyOnsiteGen: cloneInputObject(solarWind.inverterEfficiencyOnsiteGen) ?? target.inverterEfficiencyOnsiteGen,
        ...colocatedOnsiteFields,
        assetPolicySupportType: solarWind.assetPolicySupportType,
        assetHasCfd: solarWind.assetHasCfd,
        cfdStrikePrice: cloneInputObject(solarWind.cfdStrikePrice) ?? target.cfdStrikePrice,
        cfdContractLength: solarWind.cfdContractLength,
        cfdContractStart: solarWind.cfdContractStart,
        competeInDayAhead: solarWind.competeInDayAhead,
        applyCustomResLoadFactor: solarWind.applyCustomResLoadFactor,
        customResLoadFactor: cloneInputObject(solarWind.customResLoadFactor) ?? target.customResLoadFactor,
    };

    if (country === 'GB' && solarWind.country === 'GB') {
        return alignBatterySiteCapacitiesWithExport({
            ...merged,
            cmContract: solarWind.cmContract,
            cmContractStart: solarWind.cmContractStart,
            tnuosZone: solarWind.tnuosZone,
            tlfZone: solarWind.tlfZone,
            overrideDuos: solarWind.overrideDuos,
            duosCapacityCharge: cloneInputObject(solarWind.duosCapacityCharge) ?? target.duosCapacityCharge,
            duosImportCharge: cloneInputObject(solarWind.duosImportCharge) ?? target.duosImportCharge,
            duosExportCredit: cloneInputObject(solarWind.duosExportCredit) ?? target.duosExportCredit,
        });
    }

    return alignBatterySiteCapacitiesWithExport(merged);
}

export function buildAssetPayloadForSave (configurationData: IAssetStorecastData): IAssetStorecastData {
    const payload = JSON.parse(JSON.stringify(configurationData)) as IAssetStorecastData;

    payload.inverterEfficiencyOnsiteGen = {
        ...payload.inverterEfficiencyOnsiteGen,
        values: [...payload.inverterEfficiencyOnsiteGen.values.map((i) => i / 100)],
    };

    if (payload.customResLoadFactor) {
        payload.customResLoadFactor = {
            ...payload.customResLoadFactor,
            values: [...payload.customResLoadFactor.values.map((i) => i / 100)],
        };
    }

    payload.startDate = dayjs(payload.startDate).format('YYYY-MM-DD');
    payload.endDateInclusive = dayjs(payload.endDateInclusive).format('YYYY-MM-DD');
    payload.cfdContractStart = payload.cfdContractStart
        ? dayjs(payload.cfdContractStart).format('YYYY-MM-DD')
        : payload.cfdContractStart;

    if (payload.country === 'GB') {
        payload.cmContractStart = payload.cmContractStart
            ? dayjs(payload.cmContractStart).format('YYYY-MM-DD')
            : payload.cmContractStart;
    }

    if (!payload.cfdStrikePrice) {
        payload.cfdStrikePrice = defaultInputObject(0);
    }

    return payload;
}
